import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaCloudUploadAlt, FaRedo, FaTimes } from "react-icons/fa";
import { errorMessage } from "../dmApi";
import { MEDIA_ACCEPT, fileContentType, formatBytes, mediaKindOf, uploadDmMedia } from "../../../utils/directUpload";

let nextId = 1;

function checkFile(file, limits) {
  const type = fileContentType(file);
  const kind = mediaKindOf(type);
  const rule = kind && limits?.[kind];
  if (!rule || (rule.types && !rule.types.includes(type))) {
    return "This file type is not supported. Use JPG, PNG, WEBP, GIF, MP4, MOV, WEBM or PDF.";
  }
  if (rule.max_bytes && file.size > rule.max_bytes) {
    return `Too large: max ${formatBytes(rule.max_bytes)} for ${kind}s.`;
  }
  return null;
}

/**
 * Drag-and-drop uploader for a DM project's media library (posters, images, videos).
 * Files go straight to object storage; `onUploaded(media)` fires for each finished file.
 */
function MediaUploader({ api, projectId, contentId, meta, onUploaded, compact = false }) {
  const [queue, setQueue] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [compress, setCompress] = useState(false);
  const inputRef = useRef(null);
  const controllers = useRef({});
  const limits = meta?.media_limits;
  const enabled = meta?.media_uploads_enabled !== false;

  useEffect(() => {
    const active = controllers.current;
    return () => Object.values(active).forEach((c) => c.abort());
  }, []);

  const patch = (id, changes) => setQueue((q) => q.map((item) => (item.id === id ? { ...item, ...changes } : item)));

  const start = useCallback(
    async (item) => {
      const controller = new AbortController();
      controllers.current[item.id] = controller;
      patch(item.id, { status: "uploading", progress: 0, error: "" });
      try {
        const media = await uploadDmMedia(api, projectId, item.file, {
          contentId,
          compress,
          signal: controller.signal,
          onProgress: (p) => patch(item.id, { progress: p }),
        });
        setQueue((q) => q.filter((x) => x.id !== item.id));
        onUploaded?.(media);
      } catch (err) {
        if (err?.name === "AbortError") {
          setQueue((q) => q.filter((x) => x.id !== item.id));
        } else {
          patch(item.id, { status: "error", error: err?.response ? errorMessage(err) : err?.message || "Upload failed" });
        }
      } finally {
        delete controllers.current[item.id];
      }
    },
    [api, projectId, contentId, compress, onUploaded]
  );

  const addFiles = (fileList) => {
    const items = Array.from(fileList || []).map((file) => {
      const error = checkFile(file, limits);
      return { id: nextId++, file, progress: 0, status: error ? "error" : "queued", error: error || "", invalid: !!error };
    });
    setQueue((q) => [...q, ...items]);
    items.filter((item) => !item.invalid).forEach(start);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (enabled) addFiles(e.dataTransfer.files);
  };

  const cancel = (item) => {
    if (controllers.current[item.id]) controllers.current[item.id].abort();
    else setQueue((q) => q.filter((x) => x.id !== item.id));
  };

  if (!enabled) {
    return <div className="dmp-media-up__off">Media uploads are not set up on this server yet. Choose from the media library or paste a link instead.</div>;
  }

  return (
    <div className={`dmp-media-up${compact ? " dmp-media-up--compact" : ""}`}>
      <div
        className={`dmp-media-up__drop${dragging ? " is-dragging" : ""}`}
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <FaCloudUploadAlt className="dmp-media-up__icon" aria-hidden="true" />
        <span className="dmp-media-up__text">
          <strong>Drop posters, images or videos</strong> or click to choose
        </span>
        {limits ? (
          <span className="dmp-media-up__hint">
            Images up to {formatBytes(limits.image?.max_bytes)}, videos up to {formatBytes(limits.video?.max_bytes)}
          </span>
        ) : null}
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={MEDIA_ACCEPT}
          hidden
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>
      <label className="dmp-media-up__opt">
        <input type="checkbox" checked={compress} onChange={(e) => setCompress(e.target.checked)} />
        Compress images to WebP (smaller files; keep off for print-quality posters)
      </label>

      {queue.length ? (
        <ul className="dmp-media-up__list">
          {queue.map((item) => (
            <li key={item.id} className={`dmp-media-up__item is-${item.status}`}>
              <div className="dmp-media-up__row">
                <span className="dmp-media-up__name" title={item.file.name}>
                  {item.file.name}
                </span>
                <span className="dmp-media-up__size">{formatBytes(item.file.size)}</span>
                {item.status === "error" && !item.invalid ? (
                  <button type="button" className="dmp-chip-btn dmp-chip-btn--icon" title="Retry" onClick={() => start(item)}>
                    <FaRedo />
                  </button>
                ) : null}
                <button
                  type="button"
                  className="dmp-chip-btn dmp-chip-btn--icon dmp-chip-btn--danger"
                  title={item.status === "uploading" ? "Cancel" : "Remove"}
                  onClick={() => cancel(item)}
                >
                  <FaTimes />
                </button>
              </div>
              {item.status === "uploading" ? (
                <div className="dmp-media-up__bar" aria-label={`${Math.round(item.progress * 100)}%`}>
                  <span style={{ width: `${Math.round(item.progress * 100)}%` }} />
                </div>
              ) : null}
              {item.error ? <div className="dmp-media-up__err">{item.error}</div> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default MediaUploader;
